/**
 * SamplingEditPsResultDialog — bulk "Edit PS Result" grid for Quality Control ›
 * Sampling Data. Converted 1:1 from Access `frmQualityQrySampling_EditManyResult_Add`
 * (wrapper "Edit - PSS Result") + subform `frmQualityQrySampling_EditManyResult`
 * (datasheet grid over the tmp_quality_sampling temp table).
 *
 * Behaviour (Access main-form btnEdit3_Click → form open):
 *   - Open with `tlcContractRef` = the selected list row's tlc_contract_ref. Empty
 *     ref → parent must warn before opening; this dialog also guards.
 *   - Load every sample with sample_status = 'In Use' sharing that contract ref into
 *     a grid (id DESC), each row carrying the four editable PS-result columns + a
 *     tick (checked_row). "Tick All" header checkbox toggles every row.
 *   - Only four columns are editable per row (Access UPDATE targets):
 *       Result Receipt Date (date) · Sample Result · Reject Reason · Client Sample Ref.
 *   - btnSave loops the TICKED rows and UPDATEs those four columns on
 *     tlc_factory.sampling (id, is_status = 0). Unticked rows are skipped.
 *
 * The other columns (Sample No, TLC Contract Ref, Consignee, Grade, Lot ID,
 * Client Lot ID) are display-only context — read-only like the Access datasheet.
 *
 * Web deviation: rows are pre-ticked (matching the sibling Edit-DO/Edit-similar-DO
 * dialogs and the common "edit results for the whole contract" intent); Access opens
 * with checked_row = -1 from btnEdit3 anyway, so this aligns.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type SamplingEditPsRow,
  type SamplingEditPsUpdateItem,
  useGetSamplingsForEditPsQuery,
  useUpdateSamplingsPsResultMutation,
} from '../../store/api/samplingEditPsApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  /** Selected list row's tlc_contract_ref — every 'In Use' sample sharing it is loaded. */
  tlcContractRef: string | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

/** Per-row editable draft + tick state, keyed by sampling id. */
interface RowDraft {
  checked: boolean
  resultReceiptDate: string // ISO YYYY-MM-DD or ''
  result: string
  rejectReason: string
  clientSampleRef: string
}

const SM = 'small' as const
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const headCellSx: SxProps<Theme> = { fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.75 }
const bodyCellSx: SxProps<Theme> = { fontSize: '0.75rem', whiteSpace: 'nowrap', py: 0.4 }
const editFieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 30, fontSize: '0.75rem' },
  '& .MuiInputBase-input': { py: 0.4, fontSize: '0.75rem' },
}

const s = (v: unknown): string => (v == null ? '' : String(v))

export default function SamplingEditPsResultDialog({ open, tlcContractRef, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [drafts, setDrafts] = useState<Record<number, RowDraft>>({})

  const ref = tlcContractRef ?? ''
  const { data, isFetching, isError } = useGetSamplingsForEditPsQuery(ref, { skip: !open || !ref })
  const rows: SamplingEditPsRow[] = useMemo(() => data?.rows ?? [], [data])

  const [updateRows, { isLoading: saving }] = useUpdateSamplingsPsResultMutation()

  // Hydrate drafts when rows arrive — pre-tick every row, pre-fill the four
  // editable columns with their current values.
  useEffect(() => {
    if (!open) return
    const next: Record<number, RowDraft> = {}
    for (const r of rows) {
      next[r.id] = {
        checked: true,
        resultReceiptDate: s(r.resultReceiptDate),
        result: s(r.result),
        rejectReason: s(r.rejectReason),
        clientSampleRef: s(r.clientSampleRef),
      }
    }
    setDrafts(next)
  }, [open, rows])

  const setRow = (id: number, patch: Partial<RowDraft>) =>
    setDrafts((p) => ({ ...p, [id]: { ...p[id], ...patch } }))

  const allChecked = rows.length > 0 && rows.every((r) => drafts[r.id]?.checked)
  const someChecked = rows.some((r) => drafts[r.id]?.checked)
  const checkedCount = rows.filter((r) => drafts[r.id]?.checked).length

  // Access tick_all_Click — toggle every row on/off.
  const toggleAll = (checked: boolean) =>
    setDrafts((p) => {
      const next = { ...p }
      for (const r of rows) next[r.id] = { ...next[r.id], checked }
      return next
    })

  const canSave = canEdit && !saving && checkedCount > 0

  const handleClose = () => { if (!saving) onClose() }

  // Access btnSave_Click — UPDATE each ticked row's four editable columns.
  const handleSave = async () => {
    if (!canSave) return
    const payload: SamplingEditPsUpdateItem[] = rows
      .filter((r) => drafts[r.id]?.checked)
      .map((r) => {
        const d = drafts[r.id]
        return {
          id: r.id,
          resultReceiptDate: d.resultReceiptDate || null,
          result: d.result.trim() || null,
          rejectReason: d.rejectReason.trim() || null,
          clientSampleRef: d.clientSampleRef.trim() || null,
        }
      })
    try {
      await updateRows({ rows: payload }).unwrap()
      snack.success('Successfully updated')
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Update failed'))
    }
  }

  // Display-only context columns (datasheet order by Top).
  const displayCols: { key: keyof SamplingEditPsRow; label: string }[] = [
    { key: 'sampleNo', label: 'Sample No' },
    { key: 'tlcContractRef', label: 'TLC Contract Ref' },
    { key: 'consignee', label: 'Consignee' },
    { key: 'grade', label: 'Grade' },
    { key: 'lotId', label: 'Lot ID' },
    { key: 'clientLotId', label: 'Client Lot ID' },
  ]

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>
          Edit - PSS Result{ref ? ` — ${ref}` : ''}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>(Only edit ticked rows)</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit sampling results.</Alert>}
        {!ref && <Alert severity="warning" sx={{ mb: 1.5 }}>Contract reference is empty.</Alert>}
        {isError && <Alert severity="error" sx={{ mb: 1.5 }}>Could not load samples for this contract.</Alert>}

        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}>
            No 'In Use' samples found for this contract.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={headCellSx}>
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      <Checkbox
                        size={SM}
                        checked={allChecked}
                        indeterminate={!allChecked && someChecked}
                        onChange={(e) => toggleAll(e.target.checked)}
                        disabled={!canEdit}
                      />
                      <span>Tick All</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={headCellSx}>Result Receipt Date (EDIT)</TableCell>
                  <TableCell sx={headCellSx}>Sample Result (EDIT)</TableCell>
                  <TableCell sx={headCellSx}>Reject Reason (EDIT)</TableCell>
                  <TableCell sx={headCellSx}>Client Sample Ref (EDIT)</TableCell>
                  {displayCols.map((c) => (
                    <TableCell key={c.key} sx={headCellSx}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const d = drafts[r.id]
                  if (!d) return null
                  return (
                    <TableRow key={r.id} hover selected={d.checked}>
                      <TableCell padding="checkbox" sx={bodyCellSx}>
                        <Checkbox
                          size={SM}
                          checked={d.checked}
                          onChange={(e) => setRow(r.id, { checked: e.target.checked })}
                          disabled={!canEdit}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <DatePicker
                          format="DD-MMM-YYYY"
                          value={d.resultReceiptDate ? dayjs(d.resultReceiptDate) : null}
                          disabled={!canEdit}
                          onChange={(date) =>
                            setRow(r.id, { resultReceiptDate: date && date.isValid() ? date.format('YYYY-MM-DD') : '' })
                          }
                          slotProps={{ textField: { size: SM, fullWidth: true, sx: editFieldSx } }}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <TextField
                          size={SM} fullWidth value={d.result} disabled={!canEdit} sx={editFieldSx}
                          onChange={(e) => setRow(r.id, { result: e.target.value })}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <TextField
                          size={SM} fullWidth value={d.rejectReason} disabled={!canEdit} sx={editFieldSx}
                          onChange={(e) => setRow(r.id, { rejectReason: e.target.value })}
                        />
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <TextField
                          size={SM} fullWidth value={d.clientSampleRef} disabled={!canEdit} sx={editFieldSx}
                          onChange={(e) => setRow(r.id, { clientSampleRef: e.target.value })}
                        />
                      </TableCell>
                      {displayCols.map((c) => (
                        <TableCell key={c.key} sx={bodyCellSx}>{s(r[c.key])}</TableCell>
                      ))}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  )
}
